"use client";

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSearchParamsManager } from "@/hooks/useSearchParams";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import { HugeiconsIcon } from "@hugeicons/react";
import { Sorting05Icon } from "@hugeicons/core-free-icons";
import { useEffect, useRef, useState } from "react";

const sortOptions = [
  { value: "popular", label: "محبوب‌ترین" },
  { value: "cheapest", label: "ارزان‌ترین" },
  { value: "expensive", label: "گران‌ترین" },
];

interface SortTabsProps {
  sort: string;
}

export function SortTabs({ sort }: SortTabsProps) {
  const { updateParam } = useSearchParamsManager();
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const tabsRef = useRef<HTMLDivElement>(null);

  const handleSortChange = (value: string) => {
    updateParam("sort", value);
  };

  useEffect(() => {
    if (tabsRef.current) {
      const activeTab = tabsRef.current.querySelector(
        `[data-state="active"]`
      ) as HTMLElement;
      if (activeTab) {
        const tabsList = tabsRef.current.querySelector(
          '[role="tablist"]'
        ) as HTMLElement;
        const tabsListRect = tabsList.getBoundingClientRect();
        const activeTabRect = activeTab.getBoundingClientRect();

        setIndicatorStyle({
          left: activeTabRect.left - tabsListRect.left,
          width: activeTabRect.width,
        });
      }
    }
  }, [sort]);

  return (
    <>
      {/* Mobile: Drawer Button */}
      <div className="lg:hidden w-full">
        <Sheet>
          <SheetTrigger asChild>
            <Button
              id="price"
              variant="pill"
              className={`transition-colors duration-500`}
            >
              <HugeiconsIcon icon={Sorting05Icon} className="size-[18px]" />
              <Typography variant="captionMedium">مرتب سازی</Typography>
            </Button>
          </SheetTrigger>
          <SheetContent
            side="bottom"
            className="rounded-t-2xl p-0 max-h-[60vh]"
          >
            <SheetHeader className="border-b pb-4">
              <div className="bg-muted mx-auto mb-3.5 h-1 w-[73px] shrink-0 rounded-full" />
              <SheetTitle className="text-center w-full text-sm font-medium">
                مرتب سازی براساس
              </SheetTitle>
            </SheetHeader>
            <div className="flex flex-col pb-4">
              {sortOptions.map((s) => (
                <SheetClose asChild key={s.value}>
                  <button
                    type="button"
                    onClick={() => handleSortChange(s.value)}
                    className={`flex items-center gap-2 px-8 py-3 text-base focus:outline-none ${
                      sort === s.value ? "text-primary" : ""
                    }`}
                  >
                    <span
                      className={`size-5 border rounded-full flex items-center justify-center border-primary`}
                    >
                      {sort === s.value && (
                        <span className="bg-primary rounded-full w-3 h-3 block" />
                      )}
                    </span>
                    <span className="text-[13px]">{s.label}</span>
                  </button>
                </SheetClose>
              ))}
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Desktop: Tabs with Smooth Animation */}
      <div className="hidden lg:flex items-center gap-2">
        <Typography variant="bodySRegular">مرتب سازی:</Typography>
        <div ref={tabsRef} className="relative">
          <Tabs value={sort} onValueChange={handleSortChange}>
            <TabsList className="border border-black-50 rounded-[15px] relative bg-transparent">
              {/* Animated indicator */}
              <div
                className="absolute bottom-0 h-0.5 bg-secondary transition-all duration-300 ease-in-out rounded-full"
                style={{
                  left: `${indicatorStyle.left}px`,
                  width: `${indicatorStyle.width}px`,
                }}
              />
              {sortOptions.map((s) => (
                <TabsTrigger
                  key={s.value}
                  value={s.value}
                  className="relative z-10 data-[state=active]:bg-transparent data-[state=active]:shadow-none border-none"
                >
                  {s.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>
    </>
  );
}
